import React from 'react';
import {createView, createViewConfig, assignTokens} from '@sail/engine';
import {Button, css, view, tokens, SpinnerConfig} from '@sail/ui';
import type {ButtonProps} from '@sail/ui';
import {dotComColors} from '../theme';

export type DotComButtonProps = Omit<
  ButtonProps,
  'dangerouslySetInnerHTML' | 'contentEditable'
> & {
  /**
   * Display the animating arrow on the right side of the button?
   */
  arrow: boolean;
  disabled?: boolean;
  accentColor?: string;
};

export const DotComButtonConfig = createViewConfig({
  props: {} as DotComButtonProps,
  name: 'DotComButton',
  defaults: {
    type: 'primary',
    size: 'medium',
    arrow: true,
  },
});

const hoverArrowStyles = css({
  position: 'relative',
  top: '1px',
  marginLeft: '5px',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '2px',
});

const hoverArrowLinePathStyles = css({
  opacity: '0',
  transition: `opacity 150ms cubic-bezier(0.215, 0.61, 0.355, 1)`,
  '.DotComButton:hover:not([aria-disabled="true"]) &': {
    opacity: '1',
  },
});

const hoverArrowTipPathStyles = css({
  transform: 'none',
  transition: `transform 150ms cubic-bezier(0.215, 0.61, 0.355, 1)`,
  '.DotComButton:hover:not([aria-disabled="true"]) &': {
    transform: 'translateX(3px)',
  },
});

export const HoverArrow = createView(() => {
  return (
    <view.svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
      uses={[hoverArrowStyles]}
    >
      <g fillRule="evenodd">
        <view.path d="M0 5h7" uses={[hoverArrowLinePathStyles]} />
        <view.path d="M1 1l4 4-4 4" uses={[hoverArrowTipPathStyles]} />
      </g>
    </view.svg>
  );
});

/**
 * The primary call to action button used across stripe.com.
 */
export const DotComButton = DotComButtonConfig.createView(
  ({arrow, accentColor = dotComColors.brandButton, children, ...props}) => {
    return (
      <Button
        {...props}
        className="DotComButton"
        test-id="dotcom-button"
        css={{
          paddingX: 'medium',
          transition: `all 150ms cubic-bezier(0.215, 0.61, 0.355, 1)`,
          height: '33px',
          paddingTop: '3px',
          paddingBottom: '4px',
          borderRadius: 'rounded',
          lineHeight: 1.6,
          keyline: 'unset',
          ':focus': {
            keyline: 'unset',
            boxShadow: '0 0 0 2px #4d90fe,inset 0 0 0 2px hsla(0,0%,100%,0.9)',
            focusRing: 'none',
          },
          ':hover:not(:active):not([aria-disabled="true"])': {
            keyline: 'unset',
            topShadow: 'none',
          },
          ':active:not([aria-disabled="true"])': {
            keyline: 'unset',
            topShadow: 'none',
          },
        }}
        uses={[
          assignTokens(tokens.color.background.action.primary, accentColor),
          assignTokens(
            tokens.color.background.action.primary.hovered,
            '#0a2540',
          ),
          assignTokens(
            tokens.color.background.action.primary.pressed,
            '#2e3a55',
          ),
          SpinnerConfig.customize({
            css: {
              position: 'absolute',
              top: `50%`,
              left: `50%`,
              marginTop: '-8px',
              marginLeft: '-8px',
              color: 'onAction.primary',
            },
          }),
        ]}
      >
        {children}
        {arrow ? <HoverArrow /> : null}
      </Button>
    );
  },
  {
    css: {
      ':active': {
        keyline: 'none',
        border: 'none',
      },
    },
    forward: {arrow: true, disabled: true},
    variants: {
      arrow: {
        true: {
          css: {
            gap: 'xsmall',
            paddingRight: 'space.150',
          },
        },
      },
      disabled: {
        true: [
          css({
            '::after': {
              display: 'none',
            },
          }),
          assignTokens(tokens.color.background.action.primary, '#ABB5C5'),
          assignTokens(
            tokens.color.background.action.primary.hovered,
            '#ABB5C5',
          ),
        ],
      },
    },
  },
);
